import React from "react";
import ReactDOM from "react-dom/client";
import { Provider } from "react-redux";
import { store } from "../reduxStore";
import App from "../App";

const cssModules = import.meta.glob("../**/*.css", {
  query: "?inline",
  import: "default",
  eager: true,
}) as Record<string, string>;

type EmbedConfig = {
  key: string;
  debug?: boolean;
  apiUrl: string;
};

const ROOT_ID = "incluway-widget-root";
const HOST_ID = "incluway-widget-host";
const STYLE_ID = "incluway-widget-inline-styles";

const API_URL_DEFAULT =
  (import.meta as any).env?.VITE_API_URL || "https://api.incluway.de";

function injectWidgetStyles(shadow: ShadowRoot, debug?: boolean) {
  let style = shadow.getElementById(STYLE_ID) as HTMLStyleElement | null;

  if (!style) {
    style = document.createElement("style");
    style.id = STYLE_ID;
    shadow.prepend(style);
  }

  const css = Object.entries(cssModules)
    .sort(([a], [b]) => {
      const order = ["index.css", "style.css", "App.css"];

      const ai = order.findIndex((name) => a.endsWith(name));
      const bi = order.findIndex((name) => b.endsWith(name));

      if (ai !== -1 || bi !== -1) {
        return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
      }

      return a.localeCompare(b);
    })
    .map(([file, content]) => `\n/* ${file} */\n${content}`)
    .join("\n");

  style.textContent = css;

  if (debug) {
    console.log("[incluway][dev] injected widget css files:", {
      count: Object.keys(cssModules).length,
      length: css.length,
      files: Object.keys(cssModules),
    });
  }
}

function ensureRootAndShadow(debug?: boolean) {
  let root = document.getElementById(ROOT_ID) as HTMLDivElement | null;

  if (!root) {
    root = document.createElement("div");
    root.id = ROOT_ID;
    document.body.appendChild(root);
  }

  root.style.position = "fixed";
  root.style.left = "0";
  root.style.top = "0";
  root.style.width = "0";
  root.style.height = "0";
  root.style.zIndex = "2147483647";
  root.style.pointerEvents = "none";

  const shadow = root.shadowRoot ?? root.attachShadow({ mode: "open" });

  injectWidgetStyles(shadow, debug);

  let host = shadow.getElementById(HOST_ID) as HTMLDivElement | null;

  if (!host) {
    host = document.createElement("div");
    host.id = HOST_ID;
    shadow.appendChild(host);
  }

  host.style.pointerEvents = "auto";
  host.setAttribute("data-lenis-prevent", "true");

  const stopScrollHijack = (event: Event) => {
    event.stopPropagation();
  };

  host.addEventListener("wheel", stopScrollHijack, { passive: true });
  host.addEventListener("touchmove", stopScrollHijack, { passive: true });

  return {
    host,
    shadow,
    stop: () => {
      host.removeEventListener("wheel", stopScrollHijack);
      host.removeEventListener("touchmove", stopScrollHijack);
    },
  };
}

function findOwnScript(): HTMLScriptElement | null {
  const scripts = Array.from(document.getElementsByTagName("script"));

  return (
    scripts.find((s) => s.src.includes("widget.incluway.de/widget.js")) ||
    scripts.find((s) => s.src.includes("/widget.js") && s.dataset?.key) ||
    scripts.find((s) => s.dataset?.key) ||
    null
  );
}

function readConfig(): EmbedConfig {
  const devCfg = (window as any).__INCLUWAY_DEV_EMBED__;

  if (devCfg?.key) {
    return {
      key: String(devCfg.key),
      debug: !!devCfg.debug,
      apiUrl: API_URL_DEFAULT,
    };
  }

  const script = findOwnScript();

  if (!script) {
    throw new Error("Could not find widget <script> tag");
  }

  const key = script.dataset.key;

  if (!key) {
    throw new Error('Missing required attribute: data-key="..."');
  }

  const debug = script.dataset.debug === "true";

  return {
    key,
    debug,
    apiUrl: API_URL_DEFAULT,
  };
}

async function fetchBootstrap(cfg: EmbedConfig) {
  const url = `${cfg.apiUrl}/public/assist/${encodeURIComponent(
    cfg.key,
  )}/bootstrap`;

  const res = await fetch(url);

  if (!res.ok) {
    throw new Error(`Bootstrap failed: ${res.status}`);
  }

  return res.json();
}

export async function bootstrapAndMount() {
  const cfg = readConfig();

  if (cfg.debug) {
    console.log("[incluway][dev] cfg", cfg);
  }

  const { host, stop } = ensureRootAndShadow(cfg.debug);

  let bootstrap: any;

  try {
    bootstrap = await fetchBootstrap(cfg);

    if (cfg.debug) {
      console.log("[incluway][dev] bootstrap ok", bootstrap);
    }
  } catch (e) {
    console.error("[incluway][dev] bootstrap error", e);

    bootstrap = {
      settings: {
        advanced: {
          desktopEnabled: true,
          mobileEnabled: true,
        },
        appearance: {
          colors: {
            icon: {
              color: "#2b6dff",
            },
          },
        },
        position: {
          widgetPositionClosed: {
            desktop: {
              position: 8,
            },
            mobile: {
              position: 5,
            },
          },
        },
      },
    };
  }

  (window as any).__INCLUWAY_BOOTSTRAP__ = bootstrap?.settings ?? bootstrap;

  (window as any).__INCLUWAY_EMBED__ = {
    key: cfg.key,
    debug: cfg.debug,
    apiUrl: cfg.apiUrl,
  };

  (window as any).__INCLUWAY_SHADOW_STOP__?.();
  (window as any).__INCLUWAY_SHADOW_STOP__ = stop;

  ReactDOM.createRoot(host).render(
    <React.StrictMode>
      <Provider store={store}>
        <App />
      </Provider>
    </React.StrictMode>,
  );
}
